/** @jsx jsx */
import { jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors } from '../../../theme';

import { ServiceIconShell } from '../ServiceIconShell';
import { ServiceIconSize } from '../ServiceIcon';

/** Font size depends on the size of the icon and the number to be displayed */
/** Maps the service icon size to a specific font size. */
const fontSizeMapping = (size: ServiceIconSize, text: number) => {
  switch (size) {
    case 20:
      return 10;
    case 30:
      return text > 9 ? 12 : 16;
    case 40:
      return text > 9 ? 16 : 18;
    case 60:
      return text > 9 ? 18 : 24;
    case 80:
      return 32;
    case 120:
      return 48;
    case 150:
      return 60;
    default:
      return 16;
  }
};

const PDLFontMap = (iconSize: ServiceIconSize, collapsedIconCount: number) => {
  switch (iconSize) {
    case 20:
    case 30:
      return collapsedIconCount <= 9
        ? '--zds-small-print-3'
        : '--zds-minimal-print-3';
    case 40:
      return collapsedIconCount <= 9
        ? '--zds-paragraph-header-3'
        : '--zds-body-3';
    default:
      return '--zds-paragraph-header-3';
  }
};

const fonts = {
  paragraph3Bold: (fontSize: number) => `700 ${fontSize}px/24px Inter`,
  paragraph1Bold: (fontSize: number) => `700 ${fontSize}px/31px Inter`,
  subHeader1Bold: (fontSize: number) => `700 ${fontSize}px/30px Inter`,
};

/** Typography in v5 depends on the size of the icon */
const typography = (iconSize: ServiceIconSize, fontSize: number): string => {
  switch (iconSize) {
    case 20:
      return fonts.paragraph3Bold(fontSize);
    case 30:
      return fonts.paragraph3Bold(fontSize);
    case 40:
      return fonts.paragraph1Bold(fontSize);
    case 60:
      return fonts.subHeader1Bold(fontSize);
    case 80:
      return fonts.subHeader1Bold(fontSize);
    case 120:
      return fonts.subHeader1Bold(fontSize);
    case 150:
      return fonts.subHeader1Bold(fontSize);
  }
};

type ServiceIconNumericProps = {
  /*
   * Number to display within the icon box.
   */
  number: number;
  /**
   * Sets the width and height of the numeric icon. Only a limited selection
   * of sizes support the use of the numeric icon.
   */
  size: ServiceIconSize;
  /**
   * Controls the presence/absence of the border. It is recommended that a borderless ServiceIcon be
   * contained in a container with non primary color background.
   */
  hasBorder?: boolean;
  /**
   * Changes the background color to white (#fff)
   */
  hasWhiteBackground?: boolean;
};

const Styles = labelStyles('ServiceIconNumeric', {
  number: (
    fontSize: number,
    iconSize: ServiceIconSize,
    collapsedIconCount: number
  ) => ({
    color: Colors.neutral800,
    textIndent: 'var(--zds-space-0, -2)', // Optical alignment

    font: `var(${PDLFontMap(iconSize, collapsedIconCount)}, ${typography(iconSize, fontSize)})`,
  }),
});

export const ServiceIconNumeric = (props: ServiceIconNumericProps) => {
  const { size, hasBorder = true, hasWhiteBackground } = props;
  const label = `Plus ${props.number} more apps.`;
  const fontSize = fontSizeMapping(size, props.number);
  // If the results are greater than 99, cap the overflow result count at 99+
  const text = props.number > 99 ? `99+` : `+${props.number}`;
  return (
    <ServiceIconShell
      size={size}
      hasBorder={hasBorder}
      hasWhiteBackground={hasWhiteBackground}
    >
      <span
        aria-label={label}
        css={Styles.number(fontSize, size, props.number)}
        role="img"
        data-zds
      >
        <span aria-hidden="true" data-zds>
          {text}
        </span>
      </span>
    </ServiceIconShell>
  );
};
